import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { TapestrySidenavService } from '@predictiveindex/tapestry';
import { IconSize } from '../core-data-model/IconSize';
import { IconType } from '../core-data-model/IconType';
import { SiteNavigationService } from '../core-services';
import {
    FeatureFlags,
    FeatureFlagsService
} from '../system-services/FeatureFlags.service';
import { InitDataService } from '../system-services/InitData/InitData.service';
import { ModuleAccessService } from '../system-services/ModuleAccess/ModuleAccess.service';
import { UserProfileSettingApi } from '../system-services/UserProfileSettings/UserProfileSetting.connect';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { TranslationService } from '../translations';
import { ModuleUserManagementPageComponent } from './module-user-management-page';

type DropdownItem = {
    onClick: Function;
    text: string;
};

@Component({
    selector: 'pi-header',
    host: {
        class: ''
    },
    template: `
        <pi-company-dropdown class="piNav--Company"> </pi-company-dropdown>
        <div class="piNav--Actions">
            @if (
                !isDesignStandalone &&
                !isHireHeroStandalone &&
                !this.initData.isAbacUser() &&
                !isDiagnoseSelfServe
            ) {
                <pi-search
                    data-testing="header-Search"
                    class="piNav--Search"
                    [isDisabled]="disableSearch"
                ></pi-search>
            }
            <pi-translation-dropdown
                class="piNav--TranslationMenu"
            ></pi-translation-dropdown>
            <pi-pendo-dropdown></pi-pendo-dropdown>
            @if (showSwitchToClassicBanner) {
                <div class="piNav__switchToClassic" (click)="goToClassicSite()">
                    <span style="padding-top: 5px;">{{
                        TR.key('SwitchToClassic')
                    }}</span>
                </div>
            }
            @if (showUserDropdown()) {
                <user-dropdown class="piNav--UserMenu"></user-dropdown>
            }
            @if (
                !isTeamDiscoverySelfServe &&
                !isHireHeroSelfServe &&
                !isHireHeroStandalone
            ) {
                <abstract-icon
                    data-tracking="feedbackButton"
                    [iconType]="IconType.FeedbackCircle"
                    [iconSize]="17"
                >
                </abstract-icon>
            }
            @if (!isHireHeroSelfServe && !isHireHeroStandalone) {
                <help-link
                    style="cursor: pointer"
                    class="piNav--HelpLink"
                ></help-link>
            }
            @if (showAdministrationButton()) {
                <button
                    class="button button-sm button-circle-no-background"
                    data-tracking="adminButton"
                    (click)="goToAdministrationPage()"
                >
                    <abstract-icon
                        #generic
                        class="color-primary"
                        [iconType]="IconType.Gear"
                        [iconSize]="IconSize.Small"
                    ></abstract-icon>
                </button>
            }
            @if (isHireHeroSelfServe) {
                <my-profile-tooltip></my-profile-tooltip>
            }
            @if (showHelpIcon()) {
                <help-link
                    style="cursor: pointer"
                    class="piNav--HelpLink"
                ></help-link>
            }
        </div>
    `,
    standalone: false
})
export class HeaderComponent {
    @Input()
    disableSearch = false;

    IconType = IconType;
    IconSize = IconSize;

    dropdownList: DropdownItem[] = [];
    sideNavTitleKey = 'module-userManagement.title';

    constructor(
        public TR: TranslationService,
        public initData: InitDataService,
        private userProfileSettingApi: UserProfileSettingApi,
        public moduleAccess: ModuleAccessService,
        public userProfile: UserProfileSettingsService,
        public siteNavigation: SiteNavigationService,
        public router: Router,
        public featureFlagService: FeatureFlagsService,
        public tapestrySidenavService: TapestrySidenavService
    ) {}

    get showSwitchToClassicBanner() {
        return this.initData.account.companyAllowsSwitchSite;
    }

    get isDesignStandalone() {
        return this.moduleAccess.isDesignStandalone;
    }

    get isTeamDiscoverySelfServe() {
        return this.moduleAccess.isTeamDiscoverySelfServe;
    }

    get isHireHeroStandalone() {
        return this.moduleAccess.isHireHeroStandalone;
    }

    get isDiagnoseStandalone() {
        return this.moduleAccess.isDiagnoseStandalone;
    }

    get isDiagnoseSelfServe() {
        return this.moduleAccess.isDiagnoseSelfServe;
    }

    get isHireHeroSelfServe() {
        return this.moduleAccess.isHireHeroSelfServe;
    }

    goToClassicSite() {
        this.userProfileSettingApi.setUserIsUsingBetaSite(false);
    }

    getUserName() {
        return `${this.initData.user.fullName()}`;
    }

    getCompany() {
        return `${this.initData.user.company}`;
    }

    showUserDropdown(): boolean {
        return !this.isHireHeroSelfServe;
    }

    showAdministrationButton(): boolean {
        return (
            this.moduleAccess.isHireHeroSelfServe ||
            this.designAdminButtonCoditions() ||
            this.moduleAccess.hasDiagnose2
        );
    }

    showHelpIcon(): boolean {
        return this.isHireHeroSelfServe || this.isHireHeroStandalone;
    }

    public addSideNav(): boolean {
        return (
            this.enableUserManagementPeekPanel() &&
            (this.moduleAccess.isHireHeroSelfServe ||
                this.designAdminButtonCoditions() ||
                this.moduleAccess.hasDiagnose2)
        );
    }

    public designAdminButtonCoditions(): boolean {
        return (
            this.moduleAccess.isTeamDiscoverySelfServe ||
            this.moduleAccess.isLegacyWithDesign2
        );
    }

    public goToAdministrationPage(): void {
        if (this.enableUserManagementPeekPanel()) {
            this.tapestrySidenavService.open({
                component: ModuleUserManagementPageComponent,
                title: this.TR.key(this.sideNavTitleKey)
            });
        } else {
            this.router.navigate(['/administration']);
        }
    }

    public enableUserManagementPeekPanel(): boolean {
        return this.featureFlagService.getByFlag(
            FeatureFlags.enableSideNavUserManagement
        );
    }

}
